const {
  getCardDomains,
  getLegendTags,
  cardHasChampionTag,
  cardHasType,
  cardCost,
  isInDomainIdentity,
} = require('./helpers');

const MAIN_DECK_MIN = 40;
const RUNE_COUNT = 12;
const MAX_COPIES = 3;
const MAX_SIGNATURES = 3;
const MAX_BATTLEFIELDS = 3;

// Ban list — from rules/dynamic/constructed-ban-list.md
const BANNED_CARDS = new Set([
  'Called Shot',
  'Ekko, Recurrent',
  'Draven, Vanquisher',
  'Fight or Flight',
  'Scrapheap',
  'Stealthy Pursuer',
  'Stacked Deck',
]);
const BANNED_BATTLEFIELDS = new Set([
  'Aspirant\'s Climb',
  'Dreaming Tree',
  'Obelisk of Power',
  'Reaver\'s Row',
  'The Arena\'s Greatest',
]);
const BANNED_2V2_LEGENDS = new Set([
  'Master Yi, Wuju Bladesman',
]);

// Unique cards — max 1 copy per deck
const UNIQUE_CARDS = new Set([
  'Shurelya\'s Requiem',
  'Rabadon\'s Deathcrown',
  'Forgefire Cape',
]);
const UNLIMITED_COPY_CARDS = new Set([
  'Spiderling',
]);

function err(errors, { id, section, ruleRef, message, severity = 'error' }) {
  errors.push({ id, section, ruleRef, message, severity });
}

function countOf(card) {
  const n = Number(card && card.count);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

function validateDeck(deck, options = {}) {
  const errors = [];
  deck = deck || {};
  const mainCards = deck.mainCards || [];
  const runes = deck.runes || [];
  const battlefields = deck.battlefields || [];

  if (!deck.legend) {
    err(errors, { id: 'missing-legend', section: 'legend', ruleRef: '103.1', message: 'Seleziona un Champion Legend.' });
    return { isValid: false, errors };
  }

  if (options.mode === '2v2' && BANNED_2V2_LEGENDS.has(deck.legend.name)) {
    err(errors, {
      id: 'banned-2v2-legend',
      section: 'legend',
      ruleRef: 'ban-list-2v2',
      message: `"${deck.legend.name}" è vietata nel formato Constructed 2v2.`,
    });
  }

  const identity = new Set(getCardDomains(deck.legend));
  const legendTags = getLegendTags(deck.legend);

  if (identity.size === 0) {
    err(errors, { id: 'empty-identity', section: 'legend', ruleRef: '103.1.b', severity: 'warning', message: 'La Legend non ha domini: impossibile verificare la Domain Identity.' });
  }

  // 103.2.a — Chosen Champion
  const cc = deck.chosenChampion;
  if (!cc) {
    err(errors, { id: 'missing-chosen-champion', section: 'chosenChampion', ruleRef: '103.2.a', message: 'Designa un Chosen Champion.' });
  } else {
    if (!(cardHasType(cc, 'Unit') && cc.supertype === 'Champion')) {
      err(errors, { id: 'chosen-champion-type', section: 'chosenChampion', ruleRef: '103.2.a.2', message: `"${cc.name}" deve essere una champion unit.` });
    }
    if (!cardHasChampionTag(cc, legendTags)) {
      err(errors, { id: 'chosen-champion-tag', section: 'chosenChampion', ruleRef: '103.2.a.2', message: `"${cc.name}" deve avere il champion tag della Legend (${legendTags.join(', ')}).` });
    }
  }

  // Aggregati main deck (mainCards + 1 copia del chosen champion)
  const counts = {}; // name -> total copie
  const addCount = (name, n) => { counts[name] = (counts[name] || 0) + n; };
  let mainDeckCount = 0;

  for (const card of mainCards) {
    const n = countOf(card);
    mainDeckCount += n;
    addCount(card.name, n);

    // 103.2.c — domain identity (solo se identita nota)
    if (identity.size > 0 && !isInDomainIdentity(card, identity)) {
      err(errors, { id: `domain-${card.api_id}`, section: 'mainCards', ruleRef: '103.2.c', message: `"${card.name}" non rispetta la Domain Identity (${[...identity].join(', ')}).` });
    }
  }
  if (cc) { mainDeckCount += 1; addCount(cc.name, 1); }

  if (mainDeckCount < MAIN_DECK_MIN) {
    err(errors, { id: 'main-deck-size', section: 'mainCards', ruleRef: '103.2', message: `Il Main Deck ha ${mainDeckCount} carte, minimo ${MAIN_DECK_MIN}.` });
  }

  // 103.2.b — max 3 copie per nome
  for (const [name, total] of Object.entries(counts)) {
    if (total > MAX_COPIES && !UNLIMITED_COPY_CARDS.has(name)) {
      err(errors, { id: `copy-limit-${name}`, section: 'mainCards', ruleRef: '103.2.b', message: `"${name}" compare ${total} volte, massimo ${MAX_COPIES}.` });
    }
  }

  // Banned cards
  for (const card of mainCards) {
    if (BANNED_CARDS.has(card.name)) {
      err(errors, { id: `banned-${card.api_id}`, section: 'mainCards', ruleRef: 'ban-list', message: `"${card.name}" è vietata nel formato Constructed.` });
    }
  }
  if (cc && BANNED_CARDS.has(cc.name)) {
    err(errors, { id: 'banned-cc', section: 'chosenChampion', ruleRef: 'ban-list', message: `"${cc.name}" è vietata nel formato Constructed.` });
  }

  // Unique cards — max 1 copy
  for (const [name, total] of Object.entries(counts)) {
    if (UNIQUE_CARDS.has(name) && total > 1) {
      err(errors, { id: `unique-${name}`, section: 'mainCards', ruleRef: 'unique', message: `"${name}" è Unique: massimo 1 copia nel deck.` });
    }
  }

  // 103.2.d — Signature
  let signatureTotal = 0;
  for (const card of mainCards) {
    if (card.supertype !== 'Signature') continue;
    const n = countOf(card);
    signatureTotal += n;
    if (!cardHasChampionTag(card, legendTags)) {
      err(errors, { id: `signature-tag-${card.api_id}`, section: 'mainCards', ruleRef: '103.2.d.2', message: `La Signature "${card.name}" deve avere il champion tag della Legend.` });
    }
  }
  if (signatureTotal > MAX_SIGNATURES) {
    err(errors, { id: 'signature-limit', section: 'mainCards', ruleRef: '103.2.d.1', message: `Signature totali ${signatureTotal}, massimo ${MAX_SIGNATURES}.` });
  }

  // 103.3 — Rune Deck
  let runeCount = 0;
  for (const card of runes) {
    runeCount += countOf(card);
    if (identity.size > 0 && !isInDomainIdentity(card, identity)) {
      err(errors, { id: `rune-domain-${card.api_id}`, section: 'runes', ruleRef: '103.3.a.1', message: `La runa "${card.name}" non rispetta la Domain Identity.` });
    }
  }
  if (runeCount !== RUNE_COUNT) {
    err(errors, { id: 'rune-count', section: 'runes', ruleRef: '103.3.a', message: `Il Rune Deck ha ${runeCount} rune, devono essere esattamente ${RUNE_COUNT}.` });
  }

  // 103.4 — Battlefields: esattamente 3 per la modalità 1v1 (regola 103.4.a)
  const bfCount = battlefields.reduce((s, c) => s + countOf(c), 0);
  if (bfCount < MAX_BATTLEFIELDS) {
    err(errors, { id: 'battlefield-count-min', section: 'battlefields', ruleRef: '103.4', message: `${bfCount} battlefield, devono essere esattamente ${MAX_BATTLEFIELDS}.` });
  } else if (bfCount > MAX_BATTLEFIELDS) {
    err(errors, { id: 'battlefield-count-max', section: 'battlefields', ruleRef: '103.4', message: `${bfCount} battlefield, devono essere esattamente ${MAX_BATTLEFIELDS}.` });
  }
  const seen = new Map();
  for (const card of battlefields) {
    seen.set(card.name, (seen.get(card.name) || 0) + countOf(card));
    if (identity.size > 0 && getCardDomains(card).length > 0 && !isInDomainIdentity(card, identity)) {
      err(errors, { id: `battlefield-domain-${card.api_id}`, section: 'battlefields', ruleRef: '103.4.b', message: `Il battlefield "${card.name}" non rispetta la Domain Identity.` });
    }
    if (BANNED_BATTLEFIELDS.has(card.name)) {
      err(errors, { id: `banned-bf-${card.api_id}`, section: 'battlefields', ruleRef: 'ban-list', message: `Il battlefield "${card.name}" è vietato nel formato Constructed.` });
    }
  }
  for (const [name, n] of seen.entries()) {
    if (n > 1) {
      err(errors, { id: `battlefield-duplicate-${name}`, section: 'battlefields', ruleRef: '103.4.c', message: `Non puoi avere piu copie del battlefield "${name}".` });
    }
  }

  const isValid = !errors.some((e) => e.severity === 'error');
  return { isValid, errors };
}

module.exports = {
  validateDeck,
  BANNED_CARDS,
  BANNED_BATTLEFIELDS,
  BANNED_2V2_LEGENDS,
  UNIQUE_CARDS,
  UNLIMITED_COPY_CARDS,
};
